import { useEffect } from 'react'

import {
  DashboardModule, Form,
} from '@domain-group/suit-theme'

import { capitalizeString, sortArrayBy } from '@domain-group/javascript-utils'

import { useNotificationForm } from '@hooks/useNotification'

import EntitySelector from '@components/EntitySelector'

import { getAudience, getNotifiableTypeFromAudience, locationOptions } from '@functions/notification'

const renderOptions = (key: 'Banner' | 'Modal' | 'Sidebar') => {
  const filteredOptions = locationOptions.filter(option => option.group === key)
  const sorted = sortArrayBy(filteredOptions, 'asc', 'title')

  return sorted.map((entity) => {
    const { id, title } = entity

    return (
      <option key={id} value={id}>
        {title}
      </option>
    )
  })
}

interface NotificationDetailsProps {
  isSidebarNotification: boolean,
  notificationFormPayload: ReturnType<typeof useNotificationForm>,
}

const NotificationDetails = (props: NotificationDetailsProps) => {
  const { isSidebarNotification, notificationFormPayload } = props
  const {
    entityState: {
      audience,
      body,
      end_date,
      link,
      notifiable_id,
      notifiable_type,
      options,
      start_date,
      title,
    },
    errors,
    handlers,
    setEntityState,
  } = notificationFormPayload

  const {
    dismissable,
    location,
    primary_link_title,
    primary_link_url,
    realhub_platform,
    secondary_link_title,
    secondary_link_url,
    video_id,
    video_provider,
  } = options || {}

  useEffect(() => {
    if (!audience && notifiable_id) setEntityState({ audience: getAudience(notifiable_type) })
  }, [notifiable_type, audience])

  return (
    <DashboardModule title="Details">
      <Form padding="large">
        <Form.Row>
          <Form.Field label="* Audience" errorMessage={errors.audience}>
            <select
              name="audience"
              onChange={e => setEntityState({
                audience: e.target.value,
                notifiable_id: null,
                notifiable_type: '',
              })}
              value={audience}
            >
              <option value="">Please Select...</option>
              <option value="global">Global</option>
              <option value="agencies">Agencies</option>
              <option value="brands">Brands</option>
              <option value="providers">Providers</option>
            </select>
          </Form.Field>

          {!!audience && audience !== 'global' && (
            <EntitySelector
              callbacks={{
                // @ts-expect-error TS2322
                selectItem: (entity: { id?: number }) => setEntityState({
                  notifiable_id: entity ? entity.id : null,
                  notifiable_type: entity?.id ? getNotifiableTypeFromAudience(audience) : '',
                }),
              }}
              entityKey={audience}
              errorMessage={errors.notifiable_id}
              // @ts-expect-error TS2322
              marginTop={['large', 0]}
              searchForwardProps={{ placeholder: `All ${capitalizeString(audience)}` }}
              selectedItemId={notifiable_id}
              title={`* ${getNotifiableTypeFromAudience(audience)}`}
            />
          )}
        </Form.Row>

        <Form.Row marginTop="large">
          <Form.Field label="Location">
            {/*
             // @ts-expect-error TS2322 */}
            <select name="options.location" value={location} {...handlers}>
              <option value="">Notification List (Default)</option>
              <optgroup label="Banner">
                {renderOptions('Banner')}
              </optgroup>
              <optgroup label="Sidebar">
                {renderOptions('Sidebar')}
              </optgroup>
            </select>
          </Form.Field>

          {/*
           // @ts-expect-error TS2339 */}
          <Form.Field label="* Title" errorMessage={errors.title} marginTop={['large', 0]}>
            <input type="text" name="title" value={title} {...handlers} />
          </Form.Field>
        </Form.Row>

        <Form.Field label="Description" marginTop="large">
          {/*
           // @ts-expect-error TS2322 */}
          <textarea name="body" style={{ minHeight: 100 }} value={body} {...handlers} />
        </Form.Field>

        {!location && (
          <Form.Field label="Link" marginTop="large">
            <input type="text" name="link" value={link} {...handlers} />
          </Form.Field>
        )}

        {!!location && (
        <>
          <Form.Field label="Dismissable" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select
              data-value-type="boolean"
              name="options.dismissable"
              value={dismissable}
              {...handlers}
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </Form.Field>

          <Form.Row marginTop="large" columns="repeat(2, 1fr)">
            <Form.Field label="Start Date" errorMessage={errors.start_date} hideErrorMessage>
              <input type="date" name="start_date" value={start_date} {...handlers} />
            </Form.Field>

            {/*
             // @ts-expect-error TS2339 */}
            <Form.Field label="End Date" errorMessage={errors.end_date} marginTop={['large', 0]} hideErrorMessage>
              <input type="date" name="end_date" value={end_date} {...handlers} />
            </Form.Field>
          </Form.Row>

          <Form.Row marginTop="large">
            <Form.Field label="Primary Link Title" errorMessage={errors['options.primary_link_title']}>
              <input type="text" name="options.primary_link_title" value={primary_link_title} {...handlers} />
            </Form.Field>

            <Form.Field
              label="Secondary Link Title"
              errorMessage={errors['options.secondary_link_title']}
              // @ts-expect-error TS2322
              marginTop={['large', 0]}
            >
              <input type="text" name="options.secondary_link_title" value={secondary_link_title} {...handlers} />
            </Form.Field>
          </Form.Row>

          <Form.Row marginTop="large">
            <Form.Field label="Primary Link Url" errorMessage={errors['options.primary_link_url']}>
              <input type="text" name="options.primary_link_url" value={primary_link_url} {...handlers} />
            </Form.Field>

            <Form.Field
              label="Secondary Link Url"
              errorMessage={errors['options.secondary_link_url']}
              // @ts-expect-error TS2322
              marginTop={['large', 0]}
            >
              <input type="text" name="options.secondary_link_url" value={secondary_link_url} {...handlers} />
            </Form.Field>
          </Form.Row>

          {isSidebarNotification && (
            <Form.Row marginTop="large">
              {/*
               // @ts-expect-error TS2322 */}
              <Form.Field label="Video Id" marginTop={['large', 0]}>
                <input type="text" name="options.video_id" value={video_id} {...handlers} />
              </Form.Field>

              <Form.Field label="Video Provider">
                {/*
                 // @ts-expect-error TS2322 */}
                <select name="options.video_provider" value={video_provider} {...handlers}>
                  <option value="">Please Select...</option>
                  <option value="youtube">YouTube</option>
                  <option value="vimeo">Vimeo</option>
                </select>
              </Form.Field>
            </Form.Row>
          )}

          <Form.Row marginTop="large">
            <Form.Field label="Realhub Platform">
              {/*
               // @ts-expect-error TS2322 */}
              <select name="options.realhub_platform" value={realhub_platform} {...handlers}>
                <option value="">Legacy and Standalone</option>
                <option value="legacy">Legacy</option>
                <option value="standalone_v1">Standalone</option>
              </select>
            </Form.Field>
          </Form.Row>
        </>
        )}
      </Form>
    </DashboardModule>
  )
}

export default NotificationDetails
